/**
 * The app's toast API — level **E3** of the action-feedback norm
 * (spec `2026-08-03-action-feedback-system.md` §A3).
 *
 * Two rules make this module the single seam it is meant to be:
 *
 * 1. **It is the only sanctioned `sonner` consumer.** Everything else imports
 *    `notify`. That is what lets durations, dedup ids and the tone→variant
 *    mapping be decided once instead of at 22 call sites, and it is what makes
 *    a future Toaster change a one-file change.
 * 2. **It is the only sanctioned caller of the module-global `t()`.** Legitimate
 *    here, and only here, because every function below runs in an event handler
 *    or a mutation callback — never during a render, where the global store is
 *    process-shared on the server and unsubscribed on the client
 *    (`lib/i18n.ts:154-165,466-467`). `lib/api/http.ts:94` already does exactly
 *    this for the read-only refusal.
 *
 * Durations follow §2.2: success 4000 ms, error 10000 ms. `closeButton` is NOT
 * set per toast — the `<Toaster>` owns it, so a per-toast override would fight
 * the global configuration.
 */

import { toast } from "sonner";
import {
  isNetworkError,
  problemCode,
  problemKey,
  type ProblemRegistry,
} from "@/lib/api/problem-message";
import { t, type TranslationKey } from "@/lib/i18n";

/** Success and other "it worked" notices — long enough to read, short enough to ignore. */
const SUCCESS_DURATION_MS = 4000;
/**
 * Failures stay ~2.5× longer than a success: the user has to read a sentence,
 * decide whether to retry, and possibly copy something out of it. §2.2 pins the
 * number so no call site invents its own.
 */
const ERROR_DURATION_MS = 10000;
/** Neutral information — same weight as a success. */
const INFO_DURATION_MS = 5000;
/** Warnings block or degrade an action, so they outlive a success. */
const WARNING_DURATION_MS = 8000;

/** No registry — a call site that has no code table still gets the fallback. */
const NO_REGISTRY: ProblemRegistry = {};

/** The fallback used when a caller does not name a module-specific one. */
const GENERIC_ERROR_KEY: TranslationKey = "common.feedback.genericError";

/** Stable id for the offline notice, so a burst of failed calls shows once. */
const NETWORK_TOAST_ID = "notify:network";

/**
 * Per-toast options.
 *
 * A deliberately narrow subset of sonner's `ExternalToast`: everything else it
 * accepts is either styling (owned by the Toaster) or a foot-gun
 * (`duration: Infinity`, `closeButton`, `dismissible: false`).
 */
export interface NotifyOptions {
  /** Dedup key. Two toasts sharing an id collapse into one. */
  id?: string | number;
  /** Secondary line under the message. Must already be translated. */
  description?: string;
  /** Overrides the level default (e.g. 8000 for a success carrying a job id). */
  duration?: number;
  /** A single affordance, e.g. „Erneut versuchen". Label must be translated. */
  action?: { label: string; onClick: () => void };
}

/**
 * FNV-1a over the message text.
 *
 * Used to derive a default dedup id: three identical failures must not occupy
 * three of the five visible slots, while two *different* failures must both stay
 * visible. Hashing the message is the only signal available at this level —
 * {@link actionError} does better, because it has the problem code.
 */
function hashMessage(message: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < message.length; index += 1) {
    hash ^= message.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/** Sonner payload for one toast: level default, caller overrides, nothing else. */
function payload(defaultDuration: number, id: string | number | undefined, opts?: NotifyOptions) {
  return {
    ...(id === undefined ? {} : { id }),
    ...(opts?.description === undefined ? {} : { description: opts.description }),
    ...(opts?.action === undefined ? {} : { action: opts.action }),
    duration: opts?.duration ?? defaultDuration,
  };
}

function success(message: string, opts?: NotifyOptions): string | number {
  return toast.success(message, payload(SUCCESS_DURATION_MS, opts?.id, opts));
}

/**
 * A failure notice.
 *
 * Carries a **default dedup id derived from the message** when the caller does
 * not supply one — the generalisation of what `lib/api/http.ts:94` does by hand
 * for `tenant-read-only`. Pass an explicit `id` whenever a better key exists
 * (a problem code, a tenant-stamped id); pass a unique one to opt out.
 */
function error(message: string, opts?: NotifyOptions): string | number {
  const id = opts?.id ?? `notify:error:${hashMessage(message)}`;
  return toast.error(message, payload(ERROR_DURATION_MS, id, opts));
}

function info(message: string, opts?: NotifyOptions): string | number {
  return toast.info(message, payload(INFO_DURATION_MS, opts?.id, opts));
}

function warning(message: string, opts?: NotifyOptions): string | number {
  return toast.warning(message, payload(WARNING_DURATION_MS, opts?.id, opts));
}

/** Loading → resolved/rejected in one toast. All three texts must be translated. */
function promise<T>(
  awaited: Promise<T>,
  messages: { loading: string; success: string; error: string },
): void {
  toast.promise(awaited, messages);
}

/**
 * Turn a thrown API error into the one thing the user should read.
 *
 * Resolution order (§2.7): transport failure → „keine Verbindung"; otherwise
 * problem code → module registry → the caller's fallback. The user never sees an
 * HTTP status, `error.message`, or the backend's English `detail`; the raw error
 * goes to `console.error` so the details survive for whoever debugs it.
 *
 * The dedup id prefers the problem code over the message hash, so the *same*
 * refusal collapses even when two call sites translate it differently.
 */
function actionError(
  raw: unknown,
  fallback: TranslationKey = GENERIC_ERROR_KEY,
  registry?: ProblemRegistry,
): string | number {
  console.error("[notify] action failed", raw);

  if (isNetworkError(raw)) {
    return error(t("common.feedback.networkError"), { id: NETWORK_TOAST_ID });
  }

  const code = problemCode(raw);
  const message = t(problemKey(raw, registry ?? NO_REGISTRY, fallback));
  return code === null ? error(message) : error(message, { id: `notify:problem:${code}` });
}

/** Dismiss one toast by the id {@link error} & co. returned. */
function dismiss(id: string | number): void {
  toast.dismiss(id);
}

/**
 * Dismiss everything.
 *
 * The tenant switch needs it: a toast that lands after the switch belongs to the
 * tenant the user just left (§A6).
 */
function dismissAll(): void {
  toast.dismiss();
}

export const notify = {
  success,
  error,
  info,
  warning,
  promise,
  actionError,
  dismiss,
  dismissAll,
};
